/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useState } from 'react';

import Imgix from 'js/components/Imgix';

import { Button, Typography2, breakpoints } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';
import { Dialog, useTracker } from '@coursera/event-pulse/react';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { PrePurchaseUpsellAllPlansModal } from 'bundles/coursera-plus-common/components/elevateLTV/prePurchaseUpsell/PrePurchaseUpsellAllPlansModal';
import PrePurchaseUpsellCourseraPlusEnrollButton from 'bundles/coursera-plus-common/components/elevateLTV/prePurchaseUpsell/PrePurchaseUpsellCourseraPlusEnrollButton';
import { getCourseraPlusModalCopies } from 'bundles/coursera-plus-common/components/elevateLTV/prePurchaseUpsell/PrePurchaseUpsellEnrollCopies';
import { logo } from 'bundles/coursera-plus-common/utils/courseraPlusUtils';
import { useGetCourseraPlusProductPricesAndConfig } from 'bundles/coursera-plus-common/utils/prePurchaseUpsellUtils';
import { getEnrollModalName } from 'bundles/enroll-modal/utils/eventingUtils';

import _t from 'i18n!nls/coursera-plus-common';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

const { HeadingGroup, Content, Actions } = Dialog;

const styles = {
  dialog: css`
    .cds-Dialog-dialog {
      max-width: 640px !important;
    }
  `,
  topLogo: css`
    margin-bottom: var(--cds-spacing-150);
    max-width: 100%;
    padding: 0 48px;
    ${breakpoints.down('sm')} {
      margin-top: 2px;
    }
    ${breakpoints.down('xs')} {
      padding: 0 16px;
    }
  `,
  row: css`
    display: flex;
    margin-bottom: var(--cds-spacing-200);
    gap: var(--cds-spacing-100);
  `,
  button: css`
    ${breakpoints.down('xs')} {
      width: 100%;
      max-width: 100%;
    }
  `,
  primaryValueProp: css`
    font-size: 16px;
    line-height: 20px;
    margin-bottom: 4px;
  `,
};

type PropsFromCaller = {
  isOpen?: boolean;
  handleClose: () => void;
  hasCourseraPlusFreeTrial?: boolean;
};

const PrePurchaseUpsellCourseraPlusModal = ({
  isOpen = true,
  handleClose,
  hasCourseraPlusFreeTrial = true,
}: PropsFromCaller) => {
  const track = useTracker();
  const eventingModalName = getEnrollModalName({ isUsingPrePurchaseUpsellFlow: true })!;

  const [step, setStep] = useState<1 | 2>(1);

  const {
    courseraPlusMonthlyProductPrice,
    courseraPlusMonthlyProductItemId,
    courseraPlusMonthlyPriceDisplay,
    courseraPlusAnnualPriceDisplay,
    courseraPlusAnnualPriceAsMonthlyDisplay,
    courseraPlusAnnualDiscountPercent,
    courseraPlusAnnualProductItemId,
  } = useGetCourseraPlusProductPricesAndConfig({
    hasCourseraPlusFreeTrial,
  });

  const showViewAllPlans = () => {
    track('click_button', {
      button: {
        name: 'view_all_plans',
      },
      modal: {
        name: eventingModalName,
      },
    });
    setStep(2);
  };

  const onClose = () => {
    track('click_button', {
      button: {
        name: 'dismiss',
      },
      modal: {
        name: eventingModalName,
      },
    });
    handleClose();
  };

  const isLoading = !(
    courseraPlusMonthlyProductPrice &&
    courseraPlusMonthlyPriceDisplay &&
    courseraPlusAnnualPriceAsMonthlyDisplay &&
    courseraPlusAnnualPriceDisplay &&
    courseraPlusAnnualDiscountPercent &&
    courseraPlusMonthlyProductItemId &&
    courseraPlusAnnualProductItemId
  );

  if (isLoading) {
    return null;
  }

  const copy = getCourseraPlusModalCopies({
    courseraPlusPrice: {
      amount: Number(courseraPlusMonthlyProductPrice.value),
      currencyCode: courseraPlusMonthlyProductPrice.currencyCode,
    },
    hasCourseraPlusFreeTrial,
  });

  if (step === 2) {
    return (
      <PrePurchaseUpsellAllPlansModal
        onClose={() => setStep(1)}
        showSingleProgram={false}
        courseraPlusMonthlyPriceDisplay={courseraPlusMonthlyPriceDisplay}
        courseraPlusAnnualPriceDisplay={courseraPlusAnnualPriceDisplay}
        courseraPlusAnnualPriceAsMonthlyDisplay={courseraPlusAnnualPriceAsMonthlyDisplay}
        hasCourseraPlusFreeTrial={hasCourseraPlusFreeTrial}
        courseraPlusAnnualDiscountPercent={courseraPlusAnnualDiscountPercent}
        courseraPlusAnnualProductItemId={courseraPlusAnnualProductItemId}
        courseraPlusMonthlyProductItemId={courseraPlusMonthlyProductItemId}
      />
    );
  }

  if (!isOpen) {
    return null;
  }

  return (
    <Dialog
      data-testid="coursera-plus-hero-modal"
      aria-describedby="dialog-content"
      open
      variant="standard"
      width="large"
      onClose={onClose}
      sticky="actions"
      css={styles.dialog}
      trackingData={{ modal: { name: eventingModalName } }}
    >
      <Imgix src={logo.PLUS_FULL_BLUE} alt={_t('Coursera Plus logo')} height={16} css={styles.topLogo} />
      <HeadingGroup>{copy.headline}</HeadingGroup>
      <Content>
        {copy.valueProps.map(({ header, subheader }) => (
          <div css={styles.row} key={subheader}>
            <CheckIcon
              color="interactive"
              size="small"
              css={css`
                margin-top: -2px;
                min-width: 24px;
                min-height: 24px;
              `}
            />
            <div>
              <Typography2 component="div" css={styles.primaryValueProp} variant="subtitleMedium">
                {header}
              </Typography2>
              <Typography2 component="p">{subheader}</Typography2>
            </div>
          </div>
        ))}
      </Content>
      <Actions>
        <PrePurchaseUpsellCourseraPlusEnrollButton
          trackingName="pre_purchase_upsell_coursera_plus_monthly_enroll_cta"
          variant="primary"
          css={styles.button}
          productItemId={courseraPlusMonthlyProductItemId}
          isMonthly={true}
          isPrePurchaseUpsellAllPlansShown={false}
          annualProductItemId={courseraPlusAnnualProductItemId}
        >
          {copy.primaryCta}
        </PrePurchaseUpsellCourseraPlusEnrollButton>
        <TrackedButton
          trackingName="pre_purchase_upsell_coursera_plus_modal_view_all_plans_cta"
          css={[
            styles.button,
            css`
              ${breakpoints.up('sm')} {
                margin-left: var(--cds-spacing-200);
              }
              ${breakpoints.down('xs')} {
                margin-top: var(--cds-spacing-100);
              }
            `,
          ]}
          size="medium"
          variant="secondary"
          onClick={showViewAllPlans}
        >
          {copy.secondaryCta}
        </TrackedButton>
      </Actions>
    </Dialog>
  );
};

export default PrePurchaseUpsellCourseraPlusModal;
